import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Calendar, ChevronDown, LineChart } from 'lucide-react';
import PageLayout from '../../components/layouts/PageLayout';
import PeerReviewQualityTrendChart from '../../components/analytics/PeerReviewQualityTrendChart';
import AnalyticsExportMenu, {
  AnalyticsExportFormat,
} from '../../components/analytics/AnalyticsExportMenu';
import ColumnsPicker, {
  useVisibleColumns,
  type ColumnDef,
} from '../../components/analytics/ColumnsPicker';
import { authService } from '../../auth/authService';
import { canAccessPage, isPeerReviewAnalyticsRadiologistScoped } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import { ICON_STROKE_WIDTH } from '../../constants';
import {
  getPeerReviewAnalytics,
  PeerReviewAnalyticsSummary,
  PeerReviewModalityRow,
  PeerReviewQualityTrendPoint,
  type PeerReviewAnalyticsData,
  type PeerReviewAnalyticsFilterOptions,
} from '../../services/peerReviewAnalyticsService';
import { exportPeerReviewAnalytics } from '../../utils/peerReviewAnalyticsExport';
import { defaultFromDate, todayIsoDate } from '../../utils/peerReviewAnalyticsDates';
import '../../components/analytics/analytics-theme.css';
import '../admin/ReportDeliveryAnalytics.css';
import './PeerReviewAnalytics.css';

function formatFilterDate(isoDate: string): string {
  if (!isoDate) {
    return '';
  }
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

function clampIsoDate(value: string, min?: string, max?: string): string {
  let result = value;
  if (max && result > max) {
    result = max;
  }
  if (min && result < min) {
    result = min;
  }
  return result;
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value)) {
    return '0%';
  }
  return `${value.toFixed(1)}%`;
}

const EMPTY_SUMMARY: PeerReviewAnalyticsSummary = {
  totalPeerReviews: 0,
  agreementRatePercent: 0,
  discrepancyRatePercent: 0,
  pendingPeerReviews: 0,
  avgReviewCompletionTimeMinutes: 0,
  avgReviewCompletionTimeFormatted: '0 min',
};

const STATIC_PRIORITY_OPTIONS = [
  { value: '', label: 'All priorities' },
  { value: 'routine', label: 'Routine' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'vip', label: 'VIP' },
  { value: 'mlc', label: 'MLC' },
];

const TREND_PERIOD_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

type FilterSelectOption = { value: string; label: string };

function buildModalityFilterOptions(
  modalities: PeerReviewAnalyticsFilterOptions['modalities']
): FilterSelectOption[] {
  return [
    { value: '', label: 'All modalities' },
    ...modalities.map(item => ({ value: item.value, label: item.label })),
  ];
}

function buildRadiologistFilterOptions(
  radiologists: PeerReviewAnalyticsFilterOptions['radiologists']
): FilterSelectOption[] {
  return [
    { value: '', label: 'All radiologists' },
    ...radiologists.map(item => ({ value: String(item.id), label: item.name })),
  ];
}

function buildRemarkFilterOptions(remarks: string[]): FilterSelectOption[] {
  return [
    { value: '', label: 'All remarks' },
    ...remarks.filter(item => item && item !== 'all').map(item => ({ value: item, label: item })),
  ];
}

function buildBodyPartFilterOptions(
  bodyParts: PeerReviewAnalyticsFilterOptions['bodyParts']
): FilterSelectOption[] {
  return [
    { value: '', label: 'All body parts' },
    ...bodyParts.map(item => ({ value: item.value, label: item.label })),
  ];
}

type PeerReviewAnalyticsLoadSetters = {
  setSummary: React.Dispatch<React.SetStateAction<PeerReviewAnalyticsSummary>>;
  setTrend: React.Dispatch<React.SetStateAction<PeerReviewQualityTrendPoint[]>>;
  setModalityRows: React.Dispatch<React.SetStateAction<PeerReviewModalityRow[]>>;
  setModalityOptions: React.Dispatch<React.SetStateAction<FilterSelectOption[]>>;
  setRadiologistOptions: React.Dispatch<React.SetStateAction<FilterSelectOption[]>>;
  setRemarkOptions: React.Dispatch<React.SetStateAction<FilterSelectOption[]>>;
  setBodyPartOptions: React.Dispatch<React.SetStateAction<FilterSelectOption[]>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
};

function applyPeerReviewAnalyticsSuccess(
  data: PeerReviewAnalyticsData,
  setters: PeerReviewAnalyticsLoadSetters
): void {
  setters.setSummary(data.summary);
  setters.setTrend(data.trend);
  setters.setModalityRows(data.modalityRows);
  const options = data.filterOptions;
  setters.setModalityOptions(buildModalityFilterOptions(options.modalities));
  setters.setRadiologistOptions(buildRadiologistFilterOptions(options.radiologists));
  setters.setRemarkOptions(buildRemarkFilterOptions(options.remarks));
  setters.setBodyPartOptions(buildBodyPartFilterOptions(options.bodyParts));
  setters.setError(null);
}

function applyPeerReviewAnalyticsFailure(
  message: string,
  setters: PeerReviewAnalyticsLoadSetters
): void {
  setters.setSummary(EMPTY_SUMMARY);
  setters.setTrend([]);
  setters.setModalityRows([]);
  setters.setError(message);
}

type PeerReviewTrendPeriod = 'day' | 'week' | 'month';

function renderPeerReviewTrendSection(
  loading: boolean,
  hasTrendData: boolean,
  trend: PeerReviewQualityTrendPoint[],
  trendPeriod: PeerReviewTrendPeriod
): React.ReactNode {
  if (loading) {
    return <p className="rda-loading">Loading chart…</p>;
  }
  if (!hasTrendData) {
    return <p className="rda-empty">No trend data for the selected filters.</p>;
  }
  return (
    <PeerReviewQualityTrendChart
      points={trend}
      trendPeriod={trendPeriod}
    />
  );
}

function renderModalityTableBody(
  loading: boolean,
  modalityRows: PeerReviewModalityRow[],
  visibleModalityColumns: ColumnDef<PeerReviewModalityRow>[],
  renderModalityCell: (columnKey: string, row: PeerReviewModalityRow) => React.ReactNode
): React.ReactNode {
  const columnCount = Math.max(visibleModalityColumns.length, 1);
  if (loading) {
    return (
      <tr>
        <td colSpan={columnCount}>Loading…</td>
      </tr>
    );
  }
  if (modalityRows.length === 0) {
    return (
      <tr>
        <td colSpan={columnCount}>No data for the selected filters.</td>
      </tr>
    );
  }
  return modalityRows.map(row => (
    <tr key={row.modality}>
      {visibleModalityColumns.map(column => (
        <td key={column.key}>{renderModalityCell(column.key, row)}</td>
      ))}
    </tr>
  ));
}

const MODALITY_TABLE_COLUMNS: ColumnDef<PeerReviewModalityRow>[] = [
  { key: 'modality', label: 'Modality' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'concur', label: 'Concur' },
  { key: 'minorDiscrepancy', label: 'Minor discrepancy' },
  {
    key: 'clinicallySignificantDiscrepancy',
    label: 'Clinically significant discrepancy',
  },
  { key: 'correctiveActions', label: 'Corrective actions' },
  { key: 'avgReviewTat', label: 'Avg review TAT' },
];

const CHART_LEGEND_ITEMS = [
  { color: '#22c55e', label: 'Concur' },
  { color: '#3b82f6', label: 'Minor discrepancy' },
  { color: '#ef4444', label: 'Clinically significant discrepancy' },
];

function RdaFilterDate({
  label,
  value,
  onChange,
  min,
  max,
}: Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="rda-filter-field pra-filter-field--date">
      <label
        className="rda-filter-label"
        htmlFor={`pra-date-${label}`}
      >
        {label}
      </label>
      <button
        type="button"
        className="rda-filter-control rda-filter-control--date"
        onClick={() => inputRef.current?.showPicker?.()}
      >
        <span className="rda-filter-value">{formatFilterDate(value)}</span>
        <Calendar
          className="rda-filter-icon"
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
        <input
          ref={inputRef}
          id={`pra-date-${label}`}
          type="date"
          className="rda-filter-date-input"
          value={value}
          min={min}
          max={max}
          onChange={e => onChange(e.target.value)}
        />
      </button>
    </div>
  );
}

function RdaFilterSelect({
  label,
  value,
  onChange,
  options,
}: Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}>) {
  const selectId = `pra-select-${label.replaceAll(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="rda-filter-field">
      <label
        className="rda-filter-label"
        htmlFor={selectId}
      >
        {label}
      </label>
      <div className="rda-filter-control rda-filter-control--select">
        <select
          id={selectId}
          className="rda-filter-select"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          {options.map(option => (
            <option
              key={option.value || '__all__'}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="rda-filter-icon rda-filter-icon--select"
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
      </div>
    </div>
  );
}

function labelForOption(
  options: { value: string; label: string }[],
  value: string,
  fallback: string
): string {
  if (!value) {
    return fallback;
  }
  return options.find(option => option.value === value)?.label || value;
}

export default function PeerReviewAnalytics() {
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(todayIsoDate);
  const [modality, setModality] = useState('');
  const [radiologist, setRadiologist] = useState('');
  const [remark, setRemark] = useState('');
  const [priority, setPriority] = useState('');
  const [bodyPart, setBodyPart] = useState('');
  const [trendPeriod, setTrendPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [modalityOptions, setModalityOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'All modalities' },
  ]);
  const [radiologistOptions, setRadiologistOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'All radiologists' },
  ]);
  const [remarkOptions, setRemarkOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'All remarks' },
  ]);
  const [bodyPartOptions, setBodyPartOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'All body parts' },
  ]);
  const [summary, setSummary] = useState<PeerReviewAnalyticsSummary>(EMPTY_SUMMARY);
  const [trend, setTrend] = useState<PeerReviewQualityTrendPoint[]>([]);
  const [modalityRows, setModalityRows] = useState<PeerReviewModalityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    visibleColumns: visibleModalityColumns,
    visibleKeys: visibleModalityColumnKeys,
    toggleColumn: toggleModalityColumn,
  } = useVisibleColumns(MODALITY_TABLE_COLUMNS, 'peer-review-modality-columns-v2');

  const maxSelectableDate = todayIsoDate();

  const handleFromDateChange = (nextFrom: string) => {
    const clamped = clampIsoDate(nextFrom, undefined, maxSelectableDate);
    const finalFrom =
      toDate && clamped > toDate ? clampIsoDate(toDate, undefined, maxSelectableDate) : clamped;
    setFromDate(finalFrom);
    if (toDate && finalFrom > toDate) {
      setToDate(finalFrom);
    }
  };

  const handleToDateChange = (nextTo: string) => {
    const clamped = clampIsoDate(nextTo, fromDate || undefined, maxSelectableDate);
    setToDate(clamped);
    if (fromDate && clamped < fromDate) {
      setFromDate(clamped);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const setters: PeerReviewAnalyticsLoadSetters = {
      setSummary,
      setTrend,
      setModalityRows,
      setModalityOptions,
      setRadiologistOptions,
      setRemarkOptions,
      setBodyPartOptions,
      setError,
    };

    const timer = globalThis.setTimeout(() => {
      setLoading(true);
      setError(null);
      getPeerReviewAnalytics({
        startDate: fromDate,
        endDate: toDate,
        modality: modality || undefined,
        radiologistId: radiologist || undefined,
        remark: remark || undefined,
        priority: priority || undefined,
        bodyPart: bodyPart || undefined,
        trendPeriod,
      })
        .then(res => {
          if (cancelled) {
            return;
          }
          if (res.success && res.data) {
            applyPeerReviewAnalyticsSuccess(res.data, setters);
            return;
          }
          applyPeerReviewAnalyticsFailure(
            res.error || 'Failed to load peer review analytics',
            setters
          );
        })
        .catch(err => {
          if (cancelled) {
            return;
          }
          const message =
            err instanceof Error ? err.message : 'Failed to load peer review analytics';
          applyPeerReviewAnalyticsFailure(message, setters);
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [fromDate, toDate, modality, radiologist, remark, priority, bodyPart, trendPeriod]);

  const hasTrendData = useMemo(
    () =>
      trend.some(
        point =>
          point.concur > 0 ||
          point.minorDiscrepancy > 0 ||
          point.clinicallySignificantDiscrepancy > 0
      ),
    [trend]
  );

  const hasExportData = useMemo(
    () => summary.totalPeerReviews > 0 || hasTrendData || modalityRows.length > 0,
    [summary.totalPeerReviews, hasTrendData, modalityRows.length]
  );

  const user = authService.getUser();
  const radiologistScoped = isPeerReviewAnalyticsRadiologistScoped(user);
  const homePath = getDefaultRoute(user);
  if (!canAccessPage(user, '/analytics/peer-review')) {
    return (
      <Navigate
        to={homePath}
        replace
      />
    );
  }

  const scopedRadiologistLabel =
    user?.name ||
    [user?.given_name, user?.family_name].filter(Boolean).join(' ') ||
    user?.username ||
    'My studies';

  const handleExport = (format: AnalyticsExportFormat) => {
    exportPeerReviewAnalytics(
      format,
      { summary, trend, modalityRows },
      {
        startDate: fromDate,
        endDate: toDate,
        modalityLabel: labelForOption(modalityOptions, modality, 'All modalities'),
        radiologistLabel: radiologistScoped
          ? scopedRadiologistLabel
          : labelForOption(radiologistOptions, radiologist, 'All radiologists'),
        remarkLabel: labelForOption(remarkOptions, remark, 'All remarks'),
        priorityLabel: labelForOption(STATIC_PRIORITY_OPTIONS, priority, 'All priorities'),
        bodyPartLabel: labelForOption(bodyPartOptions, bodyPart, 'All body parts'),
        trendPeriodLabel: labelForOption(TREND_PERIOD_OPTIONS, trendPeriod, 'Day'),
      }
    );
  };

  const renderModalityCell = (columnKey: string, row: PeerReviewModalityRow): React.ReactNode => {
    switch (columnKey) {
      case 'modality':
        return row.modality;
      case 'reviews':
        return row.reviews;
      case 'concur':
        return row.concur;
      case 'minorDiscrepancy':
        return row.minorDiscrepancy;
      case 'clinicallySignificantDiscrepancy':
        return row.clinicallySignificantDiscrepancy;
      case 'correctiveActions':
        return row.correctiveActions;
      case 'avgReviewTat':
        return row.avgReviewTatFormatted;
      default:
        return '—';
    }
  };

  return (
    <PageLayout
      title="Peer review"
      hideTitleInHeader
      contentClassName="rda-page-layout-content"
      breadcrumbs={[{ label: 'Home', path: homePath }, { label: 'Peer review' }]}
    >
      <div className="rda-dashboard">
        <header className="rda-dashboard-subheader">
          <h1 className="rda-dashboard-subheader-title">Peer review</h1>
        </header>

        <div className="rda-dashboard-container">
          <section
            className="rda-filters"
            aria-label="Peer review filters"
          >
            <RdaFilterDate
              label="From"
              value={fromDate}
              onChange={handleFromDateChange}
              max={toDate && toDate < maxSelectableDate ? toDate : maxSelectableDate}
            />
            <RdaFilterDate
              label="To"
              value={toDate}
              onChange={handleToDateChange}
              min={fromDate || undefined}
              max={maxSelectableDate}
            />
            <RdaFilterSelect
              label="Modality"
              value={modality}
              onChange={setModality}
              options={modalityOptions}
            />
            {!radiologistScoped && (
              <RdaFilterSelect
                label="Radiologist"
                value={radiologist}
                onChange={setRadiologist}
                options={radiologistOptions}
              />
            )}
            <RdaFilterSelect
              label="Remark"
              value={remark}
              onChange={setRemark}
              options={remarkOptions}
            />
            <RdaFilterSelect
              label="Priority"
              value={priority}
              onChange={setPriority}
              options={STATIC_PRIORITY_OPTIONS}
            />
            <RdaFilterSelect
              label="Body part"
              value={bodyPart}
              onChange={setBodyPart}
              options={bodyPartOptions}
            />
          </section>

          {error && (
            <p
              className="rda-error"
              role="alert"
            >
              {error}
            </p>
          )}

          <section
            className="pra-summary-cards"
            aria-label="Summary metrics"
          >
            <div className="rda-summary-card">
              <h2 className="rda-summary-card-title">Total peer reviews</h2>
              <p className="rda-summary-card-value">{loading ? '—' : summary.totalPeerReviews}</p>
            </div>
            <div className="rda-summary-card">
              <h2 className="rda-summary-card-title">Agreement rate</h2>
              <p className="rda-summary-card-value">
                {loading ? '—' : formatPercent(summary.agreementRatePercent)}
              </p>
            </div>
            <div className="rda-summary-card">
              <h2 className="rda-summary-card-title">Discrepancy rate</h2>
              <p className="rda-summary-card-value">
                {loading ? '—' : formatPercent(summary.discrepancyRatePercent)}
              </p>
            </div>
            <div className="rda-summary-card">
              <h2 className="rda-summary-card-title">Pending peer reviews</h2>
              <p className="rda-summary-card-value">{loading ? '—' : summary.pendingPeerReviews}</p>
            </div>
            <div className="rda-summary-card">
              <h2 className="rda-summary-card-title">Avg review completion time</h2>
              <p className="rda-summary-card-value">
                {loading ? '—' : summary.avgReviewCompletionTimeFormatted}
              </p>
            </div>
          </section>

          <section
            className="rda-chart-card"
            aria-label="Peer review quality trend"
          >
            <div className="pra-chart-header-row">
              <div className="rda-chart-header">
                <span
                  className="rda-chart-icon"
                  aria-hidden
                >
                  <LineChart
                    size={20}
                    strokeWidth={ICON_STROKE_WIDTH}
                  />
                </span>
                <h2 className="rda-chart-title">Peer review quality trend</h2>
              </div>
              <div className="pra-period-select-wrap">
                <select
                  id="pra-trend-period"
                  className="pra-period-select"
                  value={trendPeriod}
                  onChange={e => setTrendPeriod(e.target.value as 'day' | 'week' | 'month')}
                  aria-label="Trend period"
                >
                  {TREND_PERIOD_OPTIONS.map(option => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pra-period-select-icon"
                  size={16}
                  strokeWidth={ICON_STROKE_WIDTH}
                  aria-hidden
                />
              </div>
            </div>
            {renderPeerReviewTrendSection(loading, hasTrendData, trend, trendPeriod)}
            <div className="pra-chart-legend pra-chart-legend--bottom">
              {CHART_LEGEND_ITEMS.map(item => (
                <span
                  key={item.label}
                  className="pra-chart-legend-item"
                >
                  <span
                    className="pra-chart-legend-swatch"
                    style={{ background: item.color }}
                  />
                  {item.label}
                </span>
              ))}
            </div>
          </section>

          <section
            className="rda-table-section"
            aria-label="Peer review summary by modality"
          >
            <div className="pra-table-section-header">
              <h2 className="pra-table-section-title">Peer review summary by modality</h2>
              <div className="pra-table-toolbar analytics-section-actions">
                <ColumnsPicker
                  columns={MODALITY_TABLE_COLUMNS}
                  storageKey="peer-review-modality-columns-v2"
                  visibleKeys={visibleModalityColumnKeys}
                  toggleColumn={toggleModalityColumn}
                />
                <AnalyticsExportMenu
                  onExport={handleExport}
                  disabled={loading || !hasExportData}
                />
              </div>
            </div>
            <div className="pra-table-wrap">
              <table className="rda-table">
                <thead>
                  <tr>
                    {visibleModalityColumns.map(column => (
                      <th
                        key={column.key}
                        scope="col"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {renderModalityTableBody(
                    loading,
                    modalityRows,
                    visibleModalityColumns,
                    renderModalityCell
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}
